import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

const chf = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Budget-Vorschlag aus den Ist-Zahlen der letzten 12 abgeschlossenen Monate (pro Konto, Saisonmuster). */
export function BudgetProposalDialog({ companyId, year, onApplied }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [overwrite, setOverwrite] = useState(false);
  const [keepPattern, setKeepPattern] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    apiClient.get(`/companies/${companyId}/budgets/proposal`, { params: { year } }).then((r) => {
      setData(r.data);
      setSel(Object.fromEntries(r.data.rows.map((x) => [x.target_id, !x.already_budgeted])));
    }).catch(() => toast.error("Vorschlag konnte nicht berechnet werden"));
  }, [open, companyId, year]);

  const apply = async () => {
    const ids = Object.entries(sel).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) return toast.info("Keine Zeile ausgewählt");
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/budgets/${year}/apply-proposal`, { target_ids: ids, overwrite, keep_pattern: keepPattern });
      toast.success(`${r.data.created} Budgetzeilen angelegt${r.data.replaced ? `, ${r.data.replaced} ersetzt` : ""}${r.data.skipped ? `, ${r.data.skipped} bestehende übersprungen` : ""}`);
      setOpen(false); onApplied?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Übernehmen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const rows = data?.rows || [];
  const selTotal = rows.filter((r) => sel[r.target_id] && r.type === "expense").reduce((a, r) => a + r.annual_amount, 0);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="budget-proposal-open"><Wand2 className="h-4 w-4 mr-1" />Vorschlag aus Ist-Zahlen</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="budget-proposal-dialog">
          <DialogHeader><DialogTitle>Budget-Vorschlag {year} aus den letzten 12 Monaten</DialogTitle></DialogHeader>
          {!data ? <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Berechne …</div> : (
            <>
              <p className="text-xs text-slate-500">Basis: Ist-Buchungen {data.basis_from} bis {data.basis_to} (abgeschlossene Monate). Beträge auf CHF 10 gerundet; die Monatsverteilung folgt dem Ist-Muster (z. B. 13. Monatslohn im Dezember). Konten unter CHF 50 werden ausgelassen.</p>
              {rows.length === 0 ? <p className="text-sm text-slate-500 py-4" data-testid="budget-proposal-empty">Keine Ist-Zahlen in den letzten 12 Monaten – kein Vorschlag möglich.</p> : (
                <table className="w-full text-sm" data-testid="budget-proposal-table">
                  <thead><tr className="text-xs text-slate-500 border-b border-slate-200"><th className="py-1"><input type="checkbox" checked={rows.every((r) => sel[r.target_id])} onChange={(e) => setSel(Object.fromEntries(rows.map((r) => [r.target_id, e.target.checked])))} data-testid="budget-proposal-select-all" /></th><th className="text-left py-1 font-medium">Konto</th><th className="text-right py-1 font-medium">Ist 12 Mt.</th><th className="text-right py-1 font-medium">Vorschlag Jahr</th><th className="text-right py-1 font-medium">Monate</th><th className="py-1" /></tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.target_id} className="border-b border-slate-100" data-testid={`budget-proposal-row-${r.target_id}`}>
                        <td className="py-1"><input type="checkbox" checked={!!sel[r.target_id]} onChange={(e) => setSel({ ...sel, [r.target_id]: e.target.checked })} data-testid={`budget-proposal-check-${r.target_id}`} /></td>
                        <td className="py-1">{r.label} {r.type === "income" && <span className="text-[10px] text-emerald-700 ml-1">Ertrag</span>}</td>
                        <td className="py-1 text-right tabular-nums text-slate-500">{chf(r.actual_12m)}</td>
                        <td className="py-1 text-right tabular-nums font-medium" data-testid={`budget-proposal-amount-${r.target_id}`}>{chf(r.annual_amount)}</td>
                        <td className="py-1 text-right text-xs text-slate-500">{r.active_months}/12</td>
                        <td className="py-1 text-right text-[10px] text-amber-700">{r.already_budgeted ? "bereits budgetiert" : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-200">
                <div className="text-xs text-slate-600 space-x-4">
                  <label className="inline-flex items-center gap-1"><input type="checkbox" checked={keepPattern} onChange={(e) => setKeepPattern(e.target.checked)} data-testid="budget-proposal-pattern" />Monatsmuster übernehmen</label>
                  <label className="inline-flex items-center gap-1"><input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="budget-proposal-overwrite" />bestehende Zeilen ersetzen</label>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-600">Aufwand ausgewählt: <b data-testid="budget-proposal-total">CHF {chf(selTotal)}</b></span>
                  <Button size="sm" onClick={apply} disabled={busy || !rows.length} data-testid="budget-proposal-apply">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Als Budget übernehmen"}</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
